import { useEffect } from 'react';

interface PreloadOptions {
  href: string;
  as?: 'script' | 'style' | 'image' | 'font' | 'fetch';
  crossOrigin?: 'anonymous' | 'use-credentials';
  type?: string;
}

export const usePreload = (resources: PreloadOptions[]) => {
  useEffect(() => {
    resources.forEach(({ href, as = 'fetch', crossOrigin, type }) => {
      // Check if link already exists
      const existingLink = document.querySelector(`link[rel="preload"][href="${href}"]`);
      if (existingLink) return;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = href;
      link.as = as;
      
      if (crossOrigin) {
        link.crossOrigin = crossOrigin;
      }
      
      if (type) {
        link.type = type;
      }

      document.head.appendChild(link);
    });

    // Cleanup function
    return () => {
      resources.forEach(({ href }) => {
        const link = document.querySelector(`link[rel="preload"][href="${href}"]`);
        if (link) {
          link.remove();
        }
      });
    };
  }, [resources]);
};

// Hook for preloading critical API endpoints
export const usePreloadAPI = (endpoints: string[]) => {
  useEffect(() => {
    endpoints.forEach(endpoint => {
      // Preload API endpoints using fetch with low priority
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          fetch(endpoint, { 
            method: 'HEAD',
            priority: 'low'
          }).catch(() => {
            // Ignore errors for preload
          });
        });
      }
    });
  }, [endpoints]);
};

// Hook for preloading critical images
export const usePreloadImages = (imageUrls: string[]) => {
  useEffect(() => {
    imageUrls.forEach(url => {
      const img = new Image();
      img.src = url;
    });
  }, [imageUrls]);
};

// Hook for preloading fonts
export const usePreloadFonts = (fontUrls: string[]) => {
  useEffect(() => {
    fontUrls.forEach(url => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = url;
      link.as = 'font';
      link.type = 'font/woff2';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  }, [fontUrls]);
};
